import React from "react";
import { observer } from "mobx-react-lite";
import { Container, Stepper, Step, StepLabel, Box, Paper } from "@mui/material";
import {
    EcommerceEventList,
    Department,
    DepartmentVisibility,
} from "../../models/Event";
import { ClientConfiguration } from "../../models/ClientConfiguration";
import { useCartStore } from "../../stores/CartStore";
import TournamentSelection from "./TournamentSelection";
import GaTicketPrompt from "./GaTicketPrompt";
import TournamentCustomFields from "./TournamentCustomFields";
import TournamentCheckout from "./TournamentCheckout";
import GlobalLoading from "../GlobalLoading";
import { Toaster } from "react-hot-toast";
import PaymentProcessingStore from "../../stores/PaymentProcessingStore";
import { gtm } from "../../utils/gtm";
import ConfirmDialog from "../ConfirmDialog";

type Props = {
    eventList: EcommerceEventList;
    clientConfig: ClientConfiguration | null;
};

enum RegistrationStep {
    TOURNAMENT_SELECTION = 0,
    GA_TICKET_PROMPT = 1,
    CUSTOM_FIELDS = 2,
    CHECKOUT = 3,
}

const stepLabels = [
    "Select Tournaments",
    "General Admission",
    "Registration Details",
    "Payment",
];

const TournamentRegistrationFlow = observer(
    ({ eventList, clientConfig }: Props) => {
        const [currentStep, setCurrentStep] = React.useState<RegistrationStep>(
            RegistrationStep.TOURNAMENT_SELECTION,
        );
        const [selectedTournaments, setSelectedTournaments] = React.useState<
            Department[]
        >([]);
        const [customFieldValues, setCustomFieldValues] = React.useState<
            Record<string, Record<string, string>>
        >({});

        const cartStore = useCartStore();

        React.useEffect(() => {
            if (clientConfig) {
                PaymentProcessingStore.setClientConfig(clientConfig);
            }
            if (eventList.gtmContainerId) {
                gtm.initializeClient({
                    containerId: eventList.gtmContainerId,
                });
            }
        }, [clientConfig, eventList.gtmContainerId]);

        // Get tournament departments from event ID 1322
        const tournamentEvent = eventList.events.find(
            (event) => event.id === 1322,
        );
        const availableTournaments = tournamentEvent?.departments ?? [];

        const handleTournamentSelectionComplete = (
            tournaments: Department[],
        ) => {
            setSelectedTournaments(tournaments);
            setCurrentStep(RegistrationStep.GA_TICKET_PROMPT);
        };

        const handleGaTicketsComplete = () => {
            setCurrentStep(RegistrationStep.CUSTOM_FIELDS);
        };

        const handleCustomFieldsComplete = () => {
            setCurrentStep(RegistrationStep.CHECKOUT);
        };

        const handleBackToTournaments = () => {
            setCurrentStep(RegistrationStep.TOURNAMENT_SELECTION);
            setSelectedTournaments([]);
            setCustomFieldValues({});
            cartStore?.clearCart(false);
        };

        const renderCurrentStep = () => {
            switch (currentStep) {
                case RegistrationStep.TOURNAMENT_SELECTION:
                    return (
                        <TournamentSelection
                            tournaments={availableTournaments}
                            onSelectionComplete={
                                handleTournamentSelectionComplete
                            }
                            cartStore={cartStore}
                        />
                    );
                case RegistrationStep.GA_TICKET_PROMPT:
                    return (
                        <GaTicketPrompt
                            selectedTournaments={selectedTournaments}
                            eventList={eventList}
                            cartStore={cartStore}
                            onComplete={handleGaTicketsComplete}
                            onBack={handleBackToTournaments}
                        />
                    );
                case RegistrationStep.CUSTOM_FIELDS:
                    return (
                        <TournamentCustomFields
                            cartStore={cartStore}
                            customFieldValues={customFieldValues}
                            setCustomFieldValues={setCustomFieldValues}
                            onComplete={handleCustomFieldsComplete}
                            onBack={() =>
                                setCurrentStep(
                                    RegistrationStep.GA_TICKET_PROMPT,
                                )
                            }
                        />
                    );
                case RegistrationStep.CHECKOUT:
                    return (
                        <TournamentCheckout
                            cartStore={cartStore}
                            clientConfig={clientConfig}
                            customFieldValues={customFieldValues}
                            onBack={() =>
                                setCurrentStep(RegistrationStep.CUSTOM_FIELDS)
                            }
                        />
                    );
                default:
                    return null;
            }
        };

        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
                <Box
                    sx={{
                        mb: 4,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 3,
                    }}
                >
                    <img
                        src="https://cdn.authentigate.ca/clients/70/CNE-LOGO_DATE_SQUARE_2025.png"
                        alt="CNE 2025 Logo"
                        style={{
                            maxWidth: "120px",
                            height: "auto",
                        }}
                    />
                    <Stepper
                        activeStep={currentStep}
                        alternativeLabel
                        sx={{ width: "100%" }}
                    >
                        {stepLabels.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {renderCurrentStep()}

                <GlobalLoading />
                <Toaster position="bottom-center" />
                <ConfirmDialog />
            </Container>
        );
    },
);

export default TournamentRegistrationFlow;
